import React, { useEffect, useRef, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Image as ImageIcon,
  Refresh as RefreshIcon,
  CloudSync as CloudSyncIcon,
} from '@mui/icons-material';
import apiClient from '../lib/api';
import { toast } from 'react-hot-toast';

const ImageRegenerator = () => {
  const [cardId, setCardId] = useState('');
  const [limit, setLimit] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const pollTokenRef = useRef(0);

  useEffect(() => {
    if (!jobId) return undefined;

    const token = ++pollTokenRef.current;
    let cancelled = false;
    let timeoutId = null;

    const terminalStatus = (job) => {
      const s = (job.status || '').toString().toLowerCase();
      if (['completed', 'complete', 'succeeded', 'failed', 'error'].includes(s)) return s;
      if (job.finishedAt) {
        if (s === 'failed' || job.error) return 'failed';
        return 'completed';
      }
      // Legacy stuck jobs: last batch wrote progress (message "Processed N of N") but never ran completion.
      const p = job.progress;
      const total = typeof p?.total === 'number' ? p.total : 0;
      const doneCount = (p?.processed ?? 0) + (p?.failed ?? 0);
      if (s === 'running' && total > 0 && doneCount >= total) {
        const msg = (job.message || '').trim();
        if (/^Processed \d+ of \d+$/.test(msg)) {
          return 'completed';
        }
      }
      return null;
    };

    const poll = async () => {
      if (cancelled || token !== pollTokenRef.current) return;
      try {
        const response = await apiClient.getImageRegenerationJob(jobId);
        if (cancelled || token !== pollTokenRef.current) return;

        const job = response?.job;
        if (!job) return;

        setJobStatus(job);

        const term = terminalStatus(job);
        if (term) {
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          if (term === 'failed' || term === 'error') {
            const message = job.error || job.message || 'Image regeneration failed';
            setError(message);
            toast.error(message);
            setIsProcessing(false);
            return;
          }
          setResult({
            processed: job.progress?.processed || 0,
            failed: job.progress?.failed || 0,
            total: job.progress?.total || 0,
            results: job.resultSample || [],
          });
          toast.success(`Image regeneration complete. Processed ${job.progress?.processed || 0} image(s).`);
          setIsProcessing(false);
          return;
        }

        if (cancelled || token !== pollTokenRef.current) return;
        timeoutId = setTimeout(poll, 3000);
      } catch (err) {
        if (cancelled || token !== pollTokenRef.current) return;
        const errorMessage = err.response?.data?.message || err.message || 'Failed to poll image regeneration status';
        setError(errorMessage);
        setIsProcessing(false);
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [jobId]);

  const runRegeneration = async (params) => {
    setIsProcessing(true);
    setResult(null);
    setError(null);
    setJobStatus(null);

    try {
      const response = await apiClient.regenerateCardImages(params);
      if (!response?.jobId) {
        throw new Error('No job ID returned from server');
      }
      setJobId(response.jobId);
      toast.success('Image regeneration started. We will keep this page updated.');
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to regenerate images';
      setError(errorMessage);
      toast.error(errorMessage);
      setIsProcessing(false);
    }
  };

  const handleRegenerateAll = () => {
    setConfirmAllOpen(true);
  };

  const handleConfirmAll = async () => {
    setConfirmAllOpen(false);
    await runRegeneration({});
  };

  const handleRegenerateForCard = async (e) => {
    e.preventDefault();
    const params = {};
    if (cardId && cardId.trim()) {
      params.cardId = cardId.trim();
    } else {
      setError('Enter a card ID, or use “Regenerate all card images” for every card.');
      toast.error('Enter a card ID for single-card regeneration.');
      return;
    }
    if (limit && limit.trim()) {
      const limitNum = parseInt(limit.trim(), 10);
      if (isNaN(limitNum) || limitNum < 1) {
        setError('Limit must be a positive number');
        toast.error('Limit must be a positive number');
        return;
      }
      params.limit = limitNum;
    }
    return runRegeneration(params);
  };

  const handleReset = () => {
    setCardId('');
    setLimit('');
    setResult(null);
    setError(null);
    setJobId(null);
    setJobStatus(null);
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ImageIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="h2">
            Regenerate Images
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Rebuilds derived assets from each full-size upload: 300px thumbnails and 1500×2100 (5×7) previews.
          Original aspect ratio is preserved; the preview is letterboxed to 5×7 when needed. Jobs run async and this page polls for status updates.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <CloudSyncIcon />}
            disabled={isProcessing}
            onClick={handleRegenerateAll}
            sx={{
              alignSelf: 'flex-start',
              backgroundColor: '#2c4575',
              '&:hover': { backgroundColor: '#1e3154' },
            }}
          >
            Regenerate all card images
          </Button>
          <Typography variant="caption" color="text.secondary">
            Processes every full-size image in storage (all cards). This runs as an async job and progress updates will appear below.
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }}>or</Divider>

        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Single card (optional)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Limit processing to one card&rsquo;s images, with an optional cap for testing.
        </Typography>

        <form onSubmit={handleRegenerateForCard}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Card ID"
              value={cardId}
              onChange={(e) => setCardId(e.target.value)}
              placeholder="e.g. abc-123-uuid"
              disabled={isProcessing}
              helperText="Required for single-card run"
              fullWidth
            />

            <TextField
              label="Limit (optional)"
              type="number"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              placeholder="e.g. 50"
              disabled={isProcessing}
              helperText="Max number of images to process (within this card)"
              inputProps={{ min: 1 }}
              fullWidth
            />

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {jobStatus && isProcessing && (
              <Alert severity="info">
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Job running ({jobStatus.status})
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Job ID: {jobStatus.jobId}
                </Typography>
                <Typography variant="body2">
                  Processed: {jobStatus.progress?.processed || 0} | Failed: {jobStatus.progress?.failed || 0} | Total: {jobStatus.progress?.total || 0}
                </Typography>
              </Alert>
            )}

            {result && (
              <Alert
                severity={result.failed > 0 ? 'warning' : 'success'}
                onClose={() => setResult(null)}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Processing complete
                </Typography>
                <Typography variant="body2">
                  Processed: {result.processed || 0} | Failed: {result.failed || 0} | Total: {result.total || 0}
                </Typography>
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                type="submit"
                variant="outlined"
                startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Regenerate for this card'}
              </Button>

              {result && (
                <Button variant="outlined" onClick={handleReset} disabled={isProcessing}>
                  Reset
                </Button>
              )}
            </Box>
          </Box>
        </form>

        {result && result.results && result.results.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Details (first 10)
            </Typography>
            <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
              {result.results.slice(0, 10).map((item, index) => (
                <Typography
                  key={index}
                  variant="caption"
                  display="block"
                  sx={{
                    color: item.success ? 'success.main' : 'error.main',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                  }}
                >
                  {item.success ? '✓' : '✗'} {item.key}
                  {item.error && ` - ${item.error}`}
                </Typography>
              ))}
              {result.results.length > 10 && (
                <Typography variant="caption" color="text.secondary">
                  ... and {result.results.length - 10} more
                </Typography>
              )}
            </Box>
          </>
        )}
      </CardContent>

      <Dialog open={confirmAllOpen} onClose={() => !isProcessing && setConfirmAllOpen(false)}>
        <DialogTitle>Regenerate all card images?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will enqueue an async job to rebuild thumbnails and 5×7 preview images for every full-size file in storage across
            <strong> all cards</strong>. You can leave this page open to see live progress updates.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAllOpen(false)} disabled={isProcessing}>
            Cancel
          </Button>
          <Button onClick={handleConfirmAll} color="primary" variant="contained" disabled={isProcessing}>
            {isProcessing ? 'Running...' : 'Regenerate all'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ImageRegenerator;
